import { buildBeatGrid } from '@swarasense/shared';
import type { BeatType, Thaalam } from '@swarasense/shared';

/**
 * Pure coordinate/lookup helpers for the notation lane (Task 2's follow-along
 * practice view). Kept free of DOM so they're trivially unit-testable — see
 * `notation-lane-geometry.test.ts` — and reused by `notation-lane.ts` for both
 * laying out the beat grid and driving the cursor.
 */

/** Horizontal pixels per beat — the lane's fixed "zoom level". A drill is short
 * (a handful of beats), so this is generous enough for a comfortably tappable
 * note token; a longer exercise/song timeline (later phases) simply produces a
 * wider track that scrolls within its own container (see `.notation-scroll`
 * in styles.css) rather than shrinking notes down to illegible width. */
export const PX_PER_BEAT = 64;

/** Beat position → x pixel, at a fixed pixels-per-beat "zoom". Linear and
 * invertible; beat 0 is always x 0. */
export function beatToX(beat: number, pxPerBeat: number = PX_PER_BEAT): number {
  return beat * pxPerBeat;
}

export interface LaneTick {
  /** Beat position (0-based, may exceed one thaalam cycle for a longer timeline). */
  beat: number;
  type: BeatType;
}

/**
 * One tick per beat boundary from 0 to `Math.ceil(totalBeats)` inclusive,
 * typed sam/anga/beat by cycling the thaalam's own beat grid (`buildBeatGrid`)
 * — so a timeline longer than one cycle (e.g. a multi-avartana exercise) wraps
 * back to a fresh "sam" exactly where the metronome itself would restart the
 * cycle, keeping the lane's anga/sam markers consistent with what the student
 * actually hears. Returns `[]` for an unknown thaalam or a non-positive
 * `totalBeats` rather than throwing — a caller mid-render (no timeline loaded
 * yet) shouldn't have to guard this itself.
 */
export function buildTicks(thaalam: Thaalam | undefined, totalBeats: number): LaneTick[] {
  if (!thaalam || totalBeats <= 0) return [];
  const grid = buildBeatGrid(thaalam);
  if (grid.length === 0) return [];
  const ticks: LaneTick[] = [];
  for (let b = 0; b <= Math.ceil(totalBeats); b++) {
    ticks.push({ beat: b, type: grid[b % grid.length]!.type });
  }
  return ticks;
}

/**
 * The index of the note active at `beat`, for a `notes` array that is sorted
 * and contiguous (each note's `startBeat` is the previous note's
 * `startBeat + durationBeats` — true for every `NotationTimeline` this app
 * generates), so only `startBeat` thresholds need checking: the LAST note
 * whose `startBeat <= beat` is the active one. `beat` past the final note's
 * span (e.g. exactly `totalBeats`) clamps to that final note; an empty
 * `notes` array returns `-1` so callers can treat that as "nothing to
 * highlight" instead of crashing on `notes[-1]`.
 */
export function currentNoteIndex(
  notes: readonly { startBeat: number }[],
  beat: number,
): number {
  if (notes.length === 0) return -1;
  for (let i = notes.length - 1; i >= 0; i--) {
    if (beat >= notes[i]!.startBeat) return i;
  }
  return 0;
}
